<script lang="ts">
  import { onMount } from 'svelte';
  import Table from '../../Table.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Button from '../../Button.svelte';
  import Pagination from '../../Pagination.svelte';
  import DateRange from '../../DateRange.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import { push } from 'svelte-spa-router';
  import { DonorService, DonorTypeEnum, type Donor, EmployerUserService, type EmployerUser } from '../../../lib/api/charityServices';
  import toast from 'svelte-french-toast';

  // Extend PaginationInfo interface to match the API response
  interface ExtendedPaginationInfo {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    last_page: number;
    from: number;
    to: number;
  }
  
  // Extend EmployerUsersResponse to include employer array
  interface ExtendedEmployerUsersResponse {
    employerUsers?: {
      employer: EmployerUser[];
    };
    success: boolean;
    error?: string;
  }

  // Loading and error states
  let loading = true;
  let error: string | null = null;

  // Pagination state
  let currentPage = 1;
  let paging: ExtendedPaginationInfo | null = null;

  // Filter states
  let searchInput = '';
  let startDate = '';
  let endDate = '';

  // Dropdown options
  // let statusOptions: Array<{ uuid: string; name: string }> = [
  //   { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e643', name: 'Pending' },
  //   { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e644', name: 'Approved' },
  //   { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e645', name: 'Rejected' },
  //   { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e646', name: 'Processed' }
  // ];

  // let employerOptions = [
  //   { uuid: '', name: 'All Employers' }
  //   // Will be populated from API
  // ];

  // Donors data
  let donors: DonorRow[] = [];

  // Which breakdown the charity is viewing: by employer (aggregate) or by individual employee.
  let viewType: 'employer' | 'employee' = 'employer';

  // Display row shape for the table (extends API Donor with computed fields)
  interface DonorRow {
    donorType: string;
    donorId: string;
    donorName: string;
    donorEmail: string;
    donationCount: number;
    employeeTotalCents: number;
    employerTotalCents: number;
    totalCents: number;
    totalHours: string;
    firstDonation: string;
    lastDonation: string;
  }

  // Columns when viewing by employer (aggregate)
  const employerColumns = [
    {
      key: 'donorName',
      label: 'Employer',
      width: '18%',
      render: (value: string, row: DonorRow) => `<span class="employer-name">${value || 'N/A'}</span>`
    },
    {
      key: 'employeeTotalCents',
      label: 'Employee Total',
      width: '12%',
      render: (value: number) => `<span class="monetary-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'employerTotalCents',
      label: 'Employer Total',
      width: '12%',
      render: (value: number) => `<span class="monetary-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'totalCents',
      label: 'Total',
      width: '12%',
      render: (value: number) => `<span class="monetary-value total-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'donationCount',
      label: 'Total Donations',
      width: '10%',
      render: (value: number) => `<span class="hours-value">${value || 0}</span>`
    },
    {
      key: 'firstDonation',
      label: 'First Donation',
      width: '12%',
      render: (value: string) => `<span class="date-value">${formatDate(value)}</span>`
    },
    {
      key: 'lastDonation',
      label: 'Last Donation',
      width: '12%',
      render: (value: string) => `<span class="date-value">${formatDate(value)}</span>`
    }
  ];

  // Columns when viewing by individual employee (donor name + email)
  const employeeColumns = [
    {
      key: 'donorName',
      label: 'Employee',
      width: '20%',
      render: (value: string) => `<span class="employer-name">${value || 'N/A'}</span>`
    },
    {
      key: 'donorEmail',
      label: 'Email',
      width: '24%',
      render: (value: string) => value
        ? `<a class="email-value" href="mailto:${value}">${value}</a>`
        : '<span class="monetary-value">N/A</span>'
    },
    {
      key: 'totalCents',
      label: 'Total Donated',
      width: '14%',
      render: (value: number) => `<span class="monetary-value total-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'donationCount',
      label: 'Donations',
      width: '10%',
      render: (value: number) => `<span class="hours-value">${value || 0}</span>`
    },
    {
      key: 'firstDonation',
      label: 'First Donation',
      width: '14%',
      render: (value: string) => `<span class="date-value">${formatDate(value)}</span>`
    },
    {
      key: 'lastDonation',
      label: 'Last Donation',
      width: '14%',
      render: (value: string) => `<span class="date-value">${formatDate(value)}</span>`
    }
  ];

  $: columns = viewType === 'employee' ? employeeColumns : employerColumns;

  function setViewType(type: 'employer' | 'employee') {
    if (viewType === type) return;
    viewType = type;
    currentPage = 1;
    loadDonors();
  }

  // Format currency from cents to dollars
  function formatCurrency(cents: number): string {
    if (cents === undefined || cents === null) return '$0.00';
    
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  // Format date
  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  // Load donors from API
  async function loadDonors() {
    loading = true;
    error = null;
    
    try {
      const result = await DonorService.getDonors({
        donorType: viewType === 'employee' ? DonorTypeEnum.EMPLOYEE : DonorTypeEnum.EMPLOYER,
        page: currentPage,
        per_page: 10,
        sort_by: 'name',
        sort_direction: 'asc',
        search: searchInput || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        // employer: selectedEmployer || undefined,
        // status: selectedStatus || undefined
      });

      if (result.success && result.donors) {
        donors = result.donors.map((donor) => ({
          donorType: donor.donorType,
          donorId: donor.donorId,
          donorName: donor.donorName,
          donorEmail: donor.donorEmail ?? '',
          donationCount: donor.donationCount,
          employeeTotalCents: 0,
          employerTotalCents: donor.totalValueCents,
          totalCents: donor.totalValueCents,
          totalHours: '0',
          firstDonation: donor.firstDonation ?? '',
          lastDonation: donor.lastDonation ?? '',
        }));
        paging = result.paging as ExtendedPaginationInfo;
        
        if (paging) {
          currentPage = paging.current_page + 1;
          paging.current_page = paging.current_page + 1;
          
          // Ensure total_pages is calculated if not provided
          if (!paging.total_pages && paging.total > 0) {
            paging.total_pages = Math.ceil(paging.total / paging.per_page);
          }
          
          console.log('Updated pagination data:', {
            currentPage,
            totalPages: paging.total_pages,
            total: paging.total,
            perPage: paging.per_page,
            shouldShowPagination: paging.total > 0
          });
        }
      } else {
        error = result.error || 'Failed to fetch donor';
        donors = [];
      }
    } catch (err) {
      console.error('Error loading donors:', err);
      error = 'Failed to fetch donor';
      donors = [];
    } finally {
      loading = false;
    }
  }

  // Load employers for dropdown
  // async function loadEmployers() {
  //   try {
  //     console.log('Loading employers...');
  //     const result = await EmployerUserService.getEmployerUsers() as unknown as ExtendedEmployerUsersResponse;
  //     console.log('Employers result:', result);
      
  //     if (result.success && result.employerUsers) {
  //       console.log('Raw employer users:', result.employerUsers);
        
  //       const mappedEmployers = result.employerUsers.employer.map((user: EmployerUser) => {
  //         // Try different ways to get the name
  //         let name = 'Unknown';
          
  //         if (user.name) {
  //           name = user.name;
  //         } else if (user.full_name) {
  //           name = user.full_name;
  //         } else if (user.employer?.name) {
  //           name = user.employer.name;
  //         } else if (user.user?.name) {
  //           name = user.user.name;
  //         } else if (user.user?.full_name) {
  //           name = user.user.full_name;
  //         }
          
  //         console.log('Mapping user:', user, 'to name:', name);
          
  //         return {
  //           uuid: user.uuid,
  //           name: name
  //         };
  //       }).filter((emp: {uuid: string, name: string}) => emp.name !== 'Unknown' && emp.name !== 'All Employers'); // Filter out any with unknown names or duplicates
        
  //       console.log('Mapped employers:', mappedEmployers);
        
  //       // Reset the array completely and rebuild it
  //       employerOptions = [
  //         ...mappedEmployers
  //       ];
        
  //     } else {
  //       console.error('Failed to load employer users:', result.error);
  //     }
  //   } catch (err) {
  //     console.error('Error loading employers:', err);
  //   }
  // }

  // Handle filter changes
  function applyFilters() {
    currentPage = 1;
    loadDonors();
  }

  // function onStatusChange(value: string) {
  //   selectedStatus = value;
  //   // Don't apply filters immediately - wait for search button click
  // }

  // function onEmployerChange(value: string) {
  //   selectedEmployer = value;
  //   // Don't apply filters immediately - wait for search button click
  // }

  // Handle date range changes
  function handleDateChange(event: CustomEvent) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    // Don't apply filters immediately - wait for search button click
  }

  // Handle search input - removed debouncing, only manual search now
  // function handleSearchKeydown(event: KeyboardEvent) {
  //   if (event.key === 'Enter') {
  //     event.preventDefault();
  //     handleSearch();
  //   }
  // }

  function handleSearch() {
    applyFilters();
  }

  let downloadingReport = false;

  const EXPORT_PAGE_SIZE = 100;

  async function fetchAllDonorsForExport(): Promise<Donor[]> {
    const allDonors: Donor[] = [];
    let page = 1;
    let lastPage = 1;

    do {
      const result = await DonorService.getDonors({
        donorType: viewType === 'employee' ? DonorTypeEnum.EMPLOYEE : DonorTypeEnum.EMPLOYER,
        page,
        per_page: EXPORT_PAGE_SIZE,
        sort_by: 'name',
        sort_direction: 'asc',
        search: searchInput || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });

      if (!result.success || !result.donors) {
        const message = typeof result.error === 'string'
          ? result.error
          : 'Failed to load donors for export';
        throw new Error(message);
      }

      allDonors.push(...result.donors);

      if (result.paging) {
        lastPage = result.paging.total_pages
          ?? Math.max(1, Math.ceil((result.paging.total || 0) / EXPORT_PAGE_SIZE));
      }

      page++;
    } while (page <= lastPage);

    return allDonors;
  }

  // Generate a PDF of all donors matching the current view and filters.
  async function downloadReport() {
    downloadingReport = true;
    try {
      const exportDonors = await fetchAllDonorsForExport();

      if (exportDonors.length === 0) {
        toast.error('No results to download for the current filters.');
        return;
      }

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const primary: [number, number, number] = [42, 157, 143];

      // Header
      doc.setFillColor(primary[0], primary[1], primary[2]);
      doc.rect(0, 0, 210, 26, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('Donor Report', 15, 16);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Leave2Give', 195, 16, { align: 'right' });

      // Sub-header info
      doc.setTextColor(51, 51, 51);
      doc.setFontSize(10);
      const periodText = (startDate || endDate)
        ? `Period: ${startDate || '—'} to ${endDate || '—'}`
        : 'Period: All time';
      doc.text(periodText, 15, 34);
      doc.text(`Breakdown: ${viewType === 'employee' ? 'By Employee' : 'By Employer'}`, 15, 40);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 195, 34, { align: 'right' });
      doc.text(`Total donors: ${exportDonors.length}`, 195, 40, { align: 'right' });

      // Table header
      const isEmployee = viewType === 'employee';
      let y = 50;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setFillColor(244, 244, 244);
      doc.rect(15, y - 5, 180, 8, 'F');
      if (isEmployee) {
        doc.text('Employee', 17, y);
        doc.text('Email', 75, y);
        doc.text('Total', 150, y);
        doc.text('Donations', 172, y);
      } else {
        doc.text('Employer', 17, y);
        doc.text('Total', 150, y);
        doc.text('Donations', 172, y);
      }
      y += 8;

      doc.setFont('helvetica', 'normal');
      for (const d of exportDonors) {
        if (y > 285) {
          doc.addPage();
          y = 20;
        }
        const total = formatCurrency(d.totalValueCents ?? 0);
        doc.text(String(d.donorName || 'N/A').substring(0, 32), 17, y);
        if (isEmployee) {
          doc.text(String(d.donorEmail || 'N/A').substring(0, 38), 75, y);
        }
        doc.text(total, 150, y);
        doc.text(String(d.donationCount ?? 0), 178, y);
        y += 7;
      }

      const stamp = new Date().toISOString().slice(0, 10);
      doc.save(`donor-results-${viewType}-${stamp}.pdf`);
      toast.success('Download started');
    } catch (err) {
      console.error('Error generating donor report:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to download results. Please try again.');
    } finally {
      downloadingReport = false;
    }
  }

  // Handle search input
  function handleSearchInput(event: CustomEvent) {
    searchInput = event.detail;
    applyFilters();
  }

  // Handle pagination
  function handlePageChange(event: CustomEvent<{page: number}>) {
    currentPage = event.detail.page;
    loadDonors();
  }

  function handleRowClick(row: any) {
    if (!row || !row.donorId) return;
    // Navigate to employer view page, or the donor detail for an individual employee.
    if (row.donorType === 'employer') {
      push(`/charity/view-employer/${row.donorId}`);
    } else if (row.donorType === 'employee') {
      push(`/donor-detail/employee/${row.donorId}`);
    }
  }

  // Initialize component
  onMount(() => {
    loadDonors();
    // loadEmployers();
  });
</script>

<div class="donor-listing-container">

  <!-- View toggle: by employer (aggregate) or by individual employee (name + email) -->
  <section class="view-selector-card" aria-labelledby="donor-view-heading">
    <div class="view-selector-text">
      <h2 id="donor-view-heading" class="view-selector-title">View donors by</h2>
      <p class="view-selector-hint">
        {#if viewType === 'employer'}
          Showing donation totals grouped by employer organisation.
        {:else}
          Showing individual employees who have donated to your charity.
        {/if}
      </p>
    </div>
    <div class="view-toggle" role="tablist" aria-label="Donor breakdown">
      <button
        type="button"
        role="tab"
        class="view-toggle-btn"
        class:active={viewType === 'employer'}
        aria-selected={viewType === 'employer'}
        on:click={() => setViewType('employer')}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
        </svg>
        By Employer
      </button>
      <button
        type="button"
        role="tab"
        class="view-toggle-btn"
        class:active={viewType === 'employee'}
        aria-selected={viewType === 'employee'}
        on:click={() => setViewType('employee')}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
        </svg>
        By Employee
      </button>
    </div>
  </section>

  <!-- Filters and Search -->
  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          placeholder="Search"
          bind:value={searchInput}
          width="250px"
          on:search={handleSearchInput}
        />
      </div>
      
      <div class="filter-item date-range-wrapper">
        <DateRange
          bind:startDate
          bind:endDate
          placeholder="Date Range"
          on:change={handleDateChange}
        />
      </div>
    </div>
    
    <div class="filters-right">
      <div class="search-group">
        <Button variant="primary" on:click={handleSearch}>
          Search
        </Button>
        <Button variant="secondary" on:click={downloadReport} disabled={downloadingReport || donors.length === 0}>
          {downloadingReport ? 'Preparing…' : 'Download Results'}
        </Button>
      </div>
    </div>
  </div>

  <!-- Loading state -->
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donors...</p>
    </div>
  <!-- Error state -->
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={loadDonors}>Try Again</Button>
    </div>
  <!-- Empty state -->
  {:else if donors.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" width="48" height="48" fill="#9ca3af">
        <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
        <path d="M12 12.59l-2.3-2.3-1.4 1.42L12 15.41l5.7-5.7-1.4-1.42z"/>
      </svg>
      <p>No donors found</p>
      <Button variant="primary" on:click={handleSearch}>Reset Filters</Button>
    </div>
  <!-- Table -->
  {:else}
  <div class="table-section">
      <Table {columns} data={donors} onRowClick={handleRowClick} />
  </div>

  <!-- Pagination -->
    {#if paging && paging.total > 0}
    <div class="pagination-container">
      <Pagination 
        currentPage={currentPage} 
        totalPages={paging.total_pages || Math.ceil(paging.total / 10)} 
        on:pageChange={handlePageChange} 
      />
    </div>
    {/if}
  {/if}
</div>

<style>
  :global(.charity-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.employer-name) {
    color: #374151;
    font-weight: 400;
  }

  :global(.hours-value) {
    color: #111827;
    font-weight: 400;
  }

  :global(.monetary-value) {
    font-weight: 400;
    color: #111827;
  }

  :global(.date-value) {
    color: #6b7280;
  }

  :global(.view-button) {
    background-color: #111827;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 50px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  :global(.view-button:hover) {
    background-color: #4b5563;
  }

  :global(.total-value) {
    font-weight: 700 !important;
  }

  :global(.email-value) {
    color: #2563eb;
    text-decoration: none;
  }

  :global(.email-value:hover) {
    text-decoration: underline;
  }

  .view-selector-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    background: white;
    border: 2px solid var(--color-primary, #ffb400);
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .view-selector-text {
    flex: 1;
    min-width: 0;
  }

  .view-selector-title {
    margin: 0 0 0.25rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .view-selector-hint {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.4;
  }

  .view-toggle {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
    background-color: #f3f4f6;
    border-radius: 10px;
    padding: 0.375rem;
    border: 1px solid #e5e7eb;
  }

  .view-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: 2px solid transparent;
    background: transparent;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    white-space: nowrap;
  }

  .view-toggle-btn:hover:not(.active) {
    color: #374151;
    background-color: #e5e7eb;
  }

  .view-toggle-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.35);
  }

  .view-toggle-btn.active {
    background-color: var(--color-primary, #ffb400);
    border-color: var(--color-primary-hover, #f0a800);
    color: #111827;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  }

  .donor-listing-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: calc(100vh - 70px);
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .filter-item {
    flex: 0 0 auto;
    min-width: 200px;
  }

  /* Fix for date range component */
  .date-range-wrapper {
    flex: 0 0 auto;
  }

  .date-range-wrapper :global(.daterange-container) {
    width: 100% !important;
  }

  .filters-right {
    display: flex;
    align-items: center;
  }

  .search-group {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    margin-bottom: 1rem;
  }

  :global(.view-button-donor) {
    background-color: #111827;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 50px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .empty-state p {
    color: #6b7280;
    font-size: 1.1rem;
    margin: 1rem 0 1.5rem;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .pagination-container {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }

  /* Responsive Design */
  @media (max-width: 1280px) {
    .filters-section {
      flex-wrap: wrap;
      gap: 1rem;
    }
    
    .filters-left {
      flex: 1 1 100%;
      justify-content: flex-start;
      margin-bottom: 1rem;
    }
    
    .filters-right {
      flex: 1 1 100%;
      justify-content: flex-end;
    }

    .filter-item {
      max-width: 250px;
    }
  }

  @media (max-width: 1024px) {
    .filters-section {
      flex-direction: column;
      align-items: stretch;
    }

    .filters-left,
    .filters-right {
      width: 100%;
      justify-content: flex-start;
    }

    .filters-left {
      gap: 0.75rem;
      margin-bottom: 1rem;
    }
    
    .filter-item {
      max-width: none;
    }
    
    .date-range-wrapper {
      width: auto;
    }
    
    .search-group {
      /* width: 100%; */
      justify-content: space-between;
    }

  }

  @media (max-width: 768px) {
    .donor-listing-container {
      padding: 1rem;
      gap: 1rem;
    }

    .view-selector-card {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .view-toggle {
      flex-direction: column;
    }

    .view-toggle-btn {
      justify-content: center;
      width: 100%;
    }

    .filters-left {
      flex-direction: column;
      width: 100%;
      align-items: stretch;
    }
    
    .filter-item {
      width: 100%;
      min-width: 100%;
      margin-bottom: 0.5rem;
    }

    .search-group {
      flex-direction: column;
      align-items: stretch;
      /* width: 100%; */
      gap: 0.5rem;
    }
    
    :global(.dropdown-container) {
      width: 100% !important;
    }
    
    :global(.daterange-container) {
      width: 100% !important;
    }
  }

  @media (max-width: 480px) {
    .filters-section {
      padding: 1rem;
    }
    
    .search-group :global(button) {
      width: 100%;
    }
  }
</style> 